import React, { useState, useEffect } from 'react';
import { 
  ShieldCheckIcon,
  CogIcon,
  EyeIcon,
  EyeSlashIcon,
  UserGroupIcon,
  ChartBarIcon,
  BugAntIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  LinkIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { githubIssues } from '../../utils/githubIssues';

const AdminDashboard = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [autoFeedbackTestVisible, setAutoFeedbackTestVisible] = useState(false);
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalSessions: 0,
    errorCount: 0,
    feedbackCount: 0
  });
  const [feedbackIssues, setFeedbackIssues] = useState([]);
  const [githubConnection, setGithubConnection] = useState(null);
  const [isLoadingGithub, setIsLoadingGithub] = useState(false);
  const { adminUser, adminLogout } = useAuth();

  // 자동피드백 테스트 표시 상태 로드
  useEffect(() => {
    const savedVisibility = localStorage.getItem('autoFeedbackTestVisible');
    if (savedVisibility !== null) {
      setAutoFeedbackTestVisible(JSON.parse(savedVisibility));
    }
  }, []);

  // 시스템 통계 로드 (시뮬레이션)
  useEffect(() => {
    const loadStats = () => {
      const stats = {
        totalUsers: Math.floor(Math.random() * 1000) + 500,
        activeUsers: Math.floor(Math.random() * 50) + 20,
        totalSessions: Math.floor(Math.random() * 5000) + 2000,
        errorCount: Math.floor(Math.random() * 10) + 3,
        feedbackCount: parseInt(localStorage.getItem('betaFeedback')?.length || '0') + 
                      parseInt(localStorage.getItem('autoFeedback')?.length || '0')
      };
      setSystemStats(stats);
    };
    
    loadStats();
    const interval = setInterval(loadStats, 30000); // 30초마다 갱신
    return () => clearInterval(interval);
  }, []);

  // GitHub 연결 상태 확인 및 피드백 Issues 로드
  useEffect(() => {
    const loadGithubData = async () => {
      setIsLoadingGithub(true);
      
      try {
        // GitHub 연결 상태 확인
        const connectionStatus = await githubIssues.checkConnection();
        setGithubConnection(connectionStatus);
        
        // 연결되어 있다면 피드백 이슈들 로드
        if (connectionStatus.connected) {
          const issuesResult = await githubIssues.getFeedbackIssues(1, 10);
          setFeedbackIssues(issuesResult.issues || []);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") { console.error('GitHub 데이터 로딩 실패:', error); }
        setGithubConnection({
          connected: false,
          message: `연결 실패: ${error.message}`
        });
      } finally {
        setIsLoadingGithub(false);
      }
    };

    if (isOpen && activeTab === 'monitoring') {
      loadGithubData();
    }
  }, [isOpen, activeTab]);

  const toggleAutoFeedbackTest = () => {
    const newVisibility = !autoFeedbackTestVisible;
    setAutoFeedbackTestVisible(newVisibility);
    localStorage.setItem('autoFeedbackTestVisible', JSON.stringify(newVisibility));
    
    // 전역 이벤트 발생
    window.dispatchEvent(new CustomEvent('autoFeedbackTestToggle', {
      detail: { visible: newVisibility }
    }));
  };

  const handleLogout = () => {
    adminLogout();
    onClose();
  };

  if (!isOpen || !adminUser) return null;

  const tabs = [
    { id: 'overview', name: '대시보드', icon: ComputerDesktopIcon },
    { id: 'users', name: '사용자 관리', icon: UserGroupIcon },
    { id: 'system', name: '시스템 설정', icon: CogIcon },
    { id: 'monitoring', name: 'GitHub 피드백', icon: DocumentTextIcon }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <ShieldCheckIcon className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">관리자 대시보드</h1>
              <p className="text-red-100">{adminUser.name} ({adminUser.email})</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-sm text-red-100">
              로그인: {new Date(adminUser.loginTime).toLocaleString('ko-KR')}
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-red-600/50 hover:bg-red-600 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeftOnRectangleIcon className="w-4 h-4" />
              <span>로그아웃</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-600/50 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* 사이드바 */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* 메인 콘텐츠 */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">시스템 개요</h2>
                
                {/* 통계 카드들 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">총 사용자</p>
                        <p className="text-2xl font-bold text-blue-900">{systemStats.totalUsers.toLocaleString()}</p>
                      </div>
                      <UserGroupIcon className="w-8 h-8 text-blue-500" />
                    </div>
                  </div>

                  <div className="bg-green-50 p-6 rounded-xl border border-green-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">활성 사용자</p>
                        <p className="text-2xl font-bold text-green-900">{systemStats.activeUsers}</p>
                      </div>
                      <CheckCircleIcon className="w-8 h-8 text-green-500" />
                    </div>
                  </div>

                  <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">총 세션</p>
                        <p className="text-2xl font-bold text-purple-900">{systemStats.totalSessions.toLocaleString()}</p>
                      </div>
                      <ChartBarIcon className="w-8 h-8 text-purple-500" />
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-600 text-sm font-medium">피드백</p>
                        <p className="text-2xl font-bold text-yellow-900">{systemStats.feedbackCount}</p>
                      </div>
                      <BugAntIcon className="w-8 h-8 text-yellow-500" />
                    </div>
                  </div>
                </div>

                {/* 시스템 상태 */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">시스템 상태</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">서버 상태</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-green-600 font-medium">정상</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">데이터베이스</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-green-600 font-medium">연결됨</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">에러 수</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${systemStats.errorCount > 5 ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                        <span className={`font-medium ${systemStats.errorCount > 5 ? 'text-red-600' : 'text-yellow-600'}`}>
                          {systemStats.errorCount}개
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'system' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">시스템 설정</h2>
                
                {/* 개발자 도구 */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">개발자 도구</h3>
                  <div className="space-y-4">
                    {/* 자동피드백 테스트 토글 */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <BugAntIcon className="w-6 h-6 text-gray-500" />
                        <div>
                          <h4 className="font-medium text-gray-900">자동피드백 테스트</h4>
                          <p className="text-sm text-gray-500">개발용 자동피드백 테스트 패널 표시/숨김</p>
                        </div>
                      </div>
                      <button
                        onClick={toggleAutoFeedbackTest}
                        className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                          autoFeedbackTestVisible ? 'bg-red-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                            autoFeedbackTestVisible ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      {autoFeedbackTestVisible ? (
                        <>
                          <EyeIcon className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">자동피드백 테스트 패널이 표시됩니다</span>
                        </>
                      ) : (
                        <>
                          <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                          <span>자동피드백 테스트 패널이 숨겨집니다</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* 시스템 정보 */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">시스템 정보</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">프론트엔드 버전</span>
                      <span className="font-mono text-sm">v1.0.0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Node.js 환경</span>
                      <span className="font-mono text-sm">{process.env.NODE_ENV}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">React 버전</span>
                      <span className="font-mono text-sm">18.x</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">사용자 관리</h2>
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <p className="text-gray-500">사용자 관리 기능은 개발 중입니다.</p>
                </div>
              </div>
            )}

            {activeTab === 'monitoring' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">GitHub Issues 피드백</h2>
                  <button
                    onClick={() => {
                      const loadGithubData = async () => {
                        setIsLoadingGithub(true);
                        try {
                          const connectionStatus = await githubIssues.checkConnection();
                          setGithubConnection(connectionStatus);
                          if (connectionStatus.connected) {
                            const issuesResult = await githubIssues.getFeedbackIssues(1, 10);
                            setFeedbackIssues(issuesResult.issues || []);
                          }
                        } catch (error) {
                          if (process.env.NODE_ENV === "development") { console.error('GitHub 새로고침 실패:', error); }
                        } finally {
                          setIsLoadingGithub(false);
                        }
                      };
                      loadGithubData();
                    }}
                    disabled={isLoadingGithub}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    <svg className={`w-4 h-4 ${isLoadingGithub ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>{isLoadingGithub ? '새로고침 중...' : '새로고침'}</span>
                  </button>
                </div>

                {/* GitHub 연결 상태 */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <LinkIcon className="w-5 h-5" />
                    <span>GitHub 연결 상태</span>
                  </h3>
                  
                  {isLoadingGithub ? (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>연결 상태 확인 중...</span>
                    </div>
                  ) : githubConnection ? (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${githubConnection.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className={`font-medium ${githubConnection.connected ? 'text-green-600' : 'text-red-600'}`}>
                          {githubConnection.connected ? '연결됨' : '연결 안됨'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{githubConnection.message}</p>
                      
                      {githubConnection.connected && githubConnection.repoInfo && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <div className="text-sm space-y-1">
                            <div><span className="font-medium">레포지토리:</span> {githubConnection.repoInfo.fullName}</div>
                            <div><span className="font-medium">열린 이슈:</span> {githubConnection.repoInfo.openIssues}개</div>
                            <div><span className="font-medium">공개 상태:</span> {githubConnection.repoInfo.private ? '비공개' : '공개'}</div>
                          </div>
                        </div>
                      )}
                      
                      {!githubConnection.connected && githubConnection.config && (
                        <div className="bg-yellow-50 p-3 rounded-lg">
                          <div className="text-sm text-yellow-800">
                            <div className="font-medium mb-2">설정 상태:</div>
                            <ul className="space-y-1">
                              <li>• Token: {githubConnection.config.hasToken ? '✅ 설정됨' : '❌ 없음'}</li>
                              <li>• Owner: {githubConnection.config.hasOwner ? '✅ 설정됨' : '❌ 없음'}</li>
                              <li>• Repo: {githubConnection.config.hasRepo ? '✅ 설정됨' : '❌ 없음'}</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500">연결 상태를 확인하는 중입니다...</p>
                  )}
                </div>

                {/* GitHub Issues 목록 */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <DocumentTextIcon className="w-5 h-5" />
                    <span>최근 피드백 Issues</span>
                    <span className="text-sm text-gray-500">({feedbackIssues.length}개)</span>
                  </h3>
                  
                  {githubConnection && !githubConnection.connected ? (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                      <p className="text-gray-500">GitHub 연결이 필요합니다</p>
                      <p className="text-sm text-gray-400 mt-1">환경변수를 설정해주세요</p>
                    </div>
                  ) : feedbackIssues.length === 0 ? (
                    <div className="text-center py-8">
                      <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">아직 피드백 Issues가 없습니다</p>
                      <p className="text-sm text-gray-400 mt-1">사용자가 피드백을 보내면 자동으로 생성됩니다</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {feedbackIssues.map((issue) => (
                        <div key={issue.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-medium text-gray-900">#{issue.number}</h4>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  issue.state === 'open' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {issue.state === 'open' ? '열림' : '닫힘'}
                                </span>
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <ClockIcon className="w-3 h-3" />
                                  <span>{new Date(issue.createdAt).toLocaleString('ko-KR')}</span>
                                </div>
                              </div>
                              
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{issue.title}</p>
                              
                              {issue.labels.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {issue.labels.slice(0, 5).map((label, index) => (
                                    <span key={index} className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      <TagIcon className="w-3 h-3" />
                                      <span>{label}</span>
                                    </span>
                                  ))}
                                  {issue.labels.length > 5 && (
                                    <span className="text-xs text-gray-500">+{issue.labels.length - 5}개</span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <a
                              href={issue.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <LinkIcon className="w-4 h-4" />
                              <span>GitHub에서 보기</span>
                            </a>
                          </div>
                        </div>
                      ))}
                      
                      {githubConnection && githubConnection.connected && (
                        <div className="text-center pt-4">
                          <a
                            href={`https://github.com/${process.env.REACT_APP_GITHUB_OWNER || 'your-username'}/${process.env.REACT_APP_GITHUB_REPO || 'your-repo'}/issues?q=is%3Aissue+label%3Afeedback`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            GitHub에서 모든 피드백 Issues 보기 →
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* GitHub 사용법 가이드 */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">GitHub Issues 연동 설정</h3>
                  <div className="space-y-3 text-sm text-blue-800">
                    <p><span className="font-medium">1. 환경변수 설정:</span></p>
                    <div className="bg-blue-100 p-3 rounded font-mono text-xs">
                      REACT_APP_GITHUB_TOKEN=your_personal_access_token<br />
                      REACT_APP_GITHUB_OWNER=your_username<br />
                      REACT_APP_GITHUB_REPO=your_repo_name
                    </div>
                    <p><span className="font-medium">2. GitHub Personal Access Token:</span> Settings → Developer settings → Personal access tokens에서 생성</p>
                    <p><span className="font-medium">3. 필요 권한:</span> repo (저장소 접근), issues (이슈 관리)</p>
                    <p><span className="font-medium">4. 자동 기능:</span> 사용자 피드백이 자동으로 GitHub Issues로 생성됩니다</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;